/**
 * Mock para simular as respostas do CheckoutRestService
 * Este mock será usado quando o backend real não estiver disponível
 */

export interface MockCheckoutResponse {
  status: 'success' | 'error' | 'pending' | 'processing';
  data?: {
    charge_id: string;
    method: 'pix' | 'credit_card';
    installments?: number;
    installment_amount?: number;
    charge_status?: 'paid' | 'pending' | 'processing' | 'failed' | 'canceled' | 'chargedback';
    pix?: {
      qr_code: string;
      qr_code_text: string;
      expires_at: string;
    };
  };
  message?: string;
}

import { PaymentValidator } from '../utils/paymentValidation';
import { devLog } from '../utils/devLog';

export class CheckoutMock {
  // Armazenar cenários por charge para simular transições ao longo do tempo
  private static chargeScenarios: Record<string, { scenario: string; createdAt: number } > = {};

  // Função para gerar código PIX válido seguindo padrão BACEN
  private static generatePixCode(amount: number, pixKey: string = 'kgv@kartodromo.com.br'): string {
    // Função para calcular CRC16 (necessário para PIX)
    const calculateCRC16 = (data: string): string => {
      let crc = 0xFFFF;
      for (let i = 0; i < data.length; i++) {
        crc ^= data.charCodeAt(i) << 8;
        for (let j = 0; j < 8; j++) {
          if (crc & 0x8000) {
            crc = (crc << 1) ^ 0x1021;
          } else {
            crc = crc << 1;
          }
          crc &= 0xFFFF;
        }
      }
      return crc.toString(16).toUpperCase().padStart(4, '0');
    };

    // Construir payload PIX seguindo padrão BACEN
    const merchantAccountInfo = `0014br.gov.bcb.pix01${pixKey.length.toString().padStart(2, '0')}${pixKey}`;
    const transactionAmount = amount.toFixed(2);
    
    let payload = '';
    payload += '000201'; // Payload Format Indicator
    payload += '010212'; // Point of Initiation Method (12 = QR Code estático reutilizável)
    payload += `26${merchantAccountInfo.length.toString().padStart(2, '0')}${merchantAccountInfo}`; // Merchant Account Information
    payload += '52040000'; // Merchant Category Code
    payload += '5303986'; // Transaction Currency (986 = BRL)
    payload += `54${transactionAmount.length.toString().padStart(2, '0')}${transactionAmount}`; // Transaction Amount
    payload += '5802BR'; // Country Code
    payload += '5925KGV KARTODROMO GRANJA VI'; // Merchant Name
    payload += '6009SAO PAULO'; // Merchant City
    payload += '62070503***'; // Additional Data Field Template
    payload += '6304'; // CRC16 placeholder
    
    // Calcular CRC16 do payload sem o CRC
    const crc = calculateCRC16(payload);
    payload += crc;
    
    return payload;
  }

  // Função para gerar QR Code SVG ultra-realista baseado no exemplo
  private static generateQRCodeSVG(data: string): string {
    const size = 25; // 25x25 módulos para melhor compatibilidade
    const moduleSize = 8; // 8px por módulo para melhor visualização
    const totalSize = size * moduleSize;
    const quietZone = moduleSize * 2;
    const fullSize = totalSize + (quietZone * 2);
    
    // Inicializar matriz
    const matrix: boolean[][] = [];
    for (let y = 0; y < size; y++) {
      matrix[y] = new Array(size).fill(false);
    }
    
    // Gerar hash mais complexo baseado no data
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    
    // Função para definir módulo na matriz
    const setModule = (x: number, y: number, dark: boolean) => {
      if (x >= 0 && x < size && y >= 0 && y < size) {
        matrix[y][x] = dark;
      }
    };
    
    // 1. Finder Patterns (7x7) nos três cantos
    const addFinderPattern = (x: number, y: number) => {
      // Borda externa (7x7)
      for (let dy = 0; dy < 7; dy++) {
        for (let dx = 0; dx < 7; dx++) {
          const isEdge = dx === 0 || dx === 6 || dy === 0 || dy === 6;
          const isInner = (dx >= 2 && dx <= 4 && dy >= 2 && dy <= 4);
          setModule(x + dx, y + dy, isEdge || isInner);
        }
      }
    };
    
    addFinderPattern(0, 0);           // Top-left
    addFinderPattern(size - 7, 0);    // Top-right  
    addFinderPattern(0, size - 7);    // Bottom-left
    
    // 2. Separators (bordas brancas ao redor dos finder patterns)
    const addSeparators = () => {
      // Top-left separator
      for (let i = 0; i < 8; i++) {
        setModule(7, i, false);
        setModule(i, 7, false);
      }
      // Top-right separator
      for (let i = 0; i < 8; i++) {
        setModule(size - 8, i, false);
        setModule(size - 1 - i, 7, false);
      }
      // Bottom-left separator
      for (let i = 0; i < 8; i++) {
        setModule(7, size - 1 - i, false);
        setModule(i, size - 8, false);
      }
    };
    addSeparators();
    
    // 3. Timing Patterns (linhas alternadas)
    for (let i = 8; i < size - 8; i++) {
      setModule(i, 6, i % 2 === 0);  // Horizontal
      setModule(6, i, i % 2 === 0);  // Vertical
    }
    
    // 4. Dark Module (módulo escuro obrigatório)
    setModule(8, 4 * size + 1, true);
    
    // 5. Alignment Patterns (múltiplos padrões para versão 4)
    const alignmentPositions = [6, 26]; // Posições típicas para versão 4
    for (let i = 0; i < alignmentPositions.length; i++) {
      for (let j = 0; j < alignmentPositions.length; j++) {
        const x = alignmentPositions[i];
        const y = alignmentPositions[j];
        
        // Evitar sobreposição com finder patterns
        if ((x === 6 && y === 6) || 
            (x === 6 && y === alignmentPositions[alignmentPositions.length - 1]) ||
            (x === alignmentPositions[alignmentPositions.length - 1] && y === 6)) {
          continue;
        }
        
        // Adicionar padrão de alinhamento 5x5
        for (let dy = -2; dy <= 2; dy++) {
          for (let dx = -2; dx <= 2; dx++) {
            const dist = Math.max(Math.abs(dx), Math.abs(dy));
            const dark = dist === 2 || dist === 0;
            setModule(x + dx, y + dy, dark);
          }
        }
      }
    }
    
    // 6. Format Information (bits de formato mais realistas)
    const formatBits = [1, 1, 0, 1, 1, 0, 0, 0, 0, 0, 1, 0, 0, 1, 1];
    
    // Format info ao redor do top-left finder pattern
    for (let i = 0; i < 6; i++) {
      setModule(8, i, formatBits[i] === 1);
      setModule(i, 8, formatBits[14 - i] === 1);
    }
    setModule(8, 7, formatBits[6] === 1);
    setModule(8, 8, formatBits[7] === 1);
    setModule(7, 8, formatBits[8] === 1);
    
    // Format info espelhado
    for (let i = 0; i < 8; i++) {
      setModule(size - 1 - i, 8, formatBits[i] === 1);
      if (i < 7) {
        setModule(8, size - 7 + i, formatBits[i] === 1);
      }
    }
    
    // 7. Preencher área de dados com padrão ultra-denso e realista
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        // Verificar se a posição já foi preenchida
        let skip = false;
        
        // Finder patterns e separators
        if ((x < 9 && y < 9) || 
            (x >= size - 8 && y < 9) || 
            (x < 9 && y >= size - 8)) {
          skip = true;
        }
        
        // Timing patterns
        if (x === 6 || y === 6) {
          skip = true;
        }
        
        // Alignment patterns
        for (const pos of alignmentPositions) {
          if (Math.abs(x - pos) <= 2 && Math.abs(y - pos) <= 2) {
            skip = true;
          }
        }
        
        if (!skip) {
          // Algoritmo mais sofisticado para gerar padrão denso
          const seed1 = (hash + x * 41 + y * 37) % 1000;
          const seed2 = (hash + (x + y) * 23 + x * y * 7) % 1000;
          const seed3 = (hash + Math.abs(x - y) * 13) % 1000;
          
          // Combinar múltiplas sementes para maior complexidade
          const combined = (seed1 + seed2 + seed3) % 1000;
          
          // Criar padrões mais densos (aproximadamente 65% de módulos escuros)
          matrix[y][x] = combined > 350;
          
          // Adicionar alguns padrões específicos para maior realismo
          if ((x + y) % 3 === 0 && combined > 200) {
            matrix[y][x] = true;
          }
          if ((x * y) % 7 === 0 && combined < 800) {
            matrix[y][x] = false;
          }
        }
      }
    }
    
    // Gerar SVG otimizado
    let svg = `<svg width="${fullSize}" height="${fullSize}" xmlns="http://www.w3.org/2000/svg">`;
    svg += `<rect width="${fullSize}" height="${fullSize}" fill="white"/>`;
    
    // Desenhar módulos com otimização
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        if (matrix[y][x]) {
          const px = quietZone + (x * moduleSize);
          const py = quietZone + (y * moduleSize);
          svg += `<rect x="${px}" y="${py}" width="${moduleSize}" height="${moduleSize}" fill="black"/>`;
        }
      }
    }
    
    svg += '</svg>';
    
    // Encode SVG properly for data URL
    const encodedSvg = encodeURIComponent(svg);
    return `data:image/svg+xml,${encodedSvg}`;
  }

  static async createPayment(request: any): Promise<MockCheckoutResponse> {
    // Simular delay de rede
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Simular diferentes cenários baseados nos dados
    if (request.method_payment === 'pix') {
      // Extrair valor do pagamento
      const amount = parseFloat(request.valor_total) || parseFloat(request.amount) || 125.00;
      
      // Gerar código PIX válido
      const pixCode = this.generatePixCode(amount);
      
      // Gerar QR Code SVG baseado no código PIX
      const qrCodeSvg = this.generateQRCodeSVG(pixCode);

      return {
        status: 'pending',
        data: {
          charge_id: `ch_${Date.now()}`,
          method: 'pix',
          pix: {
            qr_code: qrCodeSvg,
            qr_code_text: pixCode,
            expires_at: new Date(Date.now() + 30 * 60 * 1000).toISOString() // 30 minutos
          }
        },
        message: 'Código PIX gerado com sucesso'
      };
    }

    if (request.method_payment === 'credit_card') {
      const total = typeof request.valor_total === 'number' ? request.valor_total : parseFloat(request.valor_total) || 0;
      const installments = typeof request.installments === 'number' ? request.installments : parseInt(String(request.installments || '1'), 10);
      const safeInstallments = isNaN(installments) || installments < 1 ? 1 : installments;
      const installmentAmount = safeInstallments > 1 ? Number((total / safeInstallments).toFixed(2)) : total;

      // Identificar cenário de teste a partir do token (mock)
      const token: string = String(request.card_token || '');
      let scenario: string | null = null;
      let testCardNumber: string | null = null;
      if (token.startsWith('testcard_')) {
        const parts = token.split('_');
        // formato: testcard_<number>_<rand>
        if (parts.length >= 3) {
          testCardNumber = parts[1];
        }
      }

      // Mapear números de cartão de teste aos cenários
      switch (testCardNumber) {
        case '4000000000000010':
          scenario = 'success';
          break;
        case '4000000000000028':
          scenario = 'fail';
          break;
        case '4000000000000036':
          scenario = 'processing_to_success';
          break;
        case '4000000000000044':
          scenario = 'processing_to_fail';
          break;
        case '4000000000000077':
          scenario = 'success_processing_to_canceled';
          break;
        case '4000000000000093':
          scenario = 'success_processing_back_to_paid';
          break;
        case '4000000000000051':
          scenario = 'processing_to_canceled';
          break;
        case '4000000000000069':
          scenario = 'paid_to_chargeback';
          break;
        default:
          scenario = null;
      }

      const chargeId = `ch_${Date.now()}`;

      if (scenario) {
        // Registrar cenário para simular nas consultas de status
        this.chargeScenarios[chargeId] = { scenario, createdAt: Date.now() };
      }

      // Determinar status inicial da cobrança
      let initialChargeStatus: 'paid' | 'pending' | 'processing' | 'failed' | 'canceled' = 'paid';
      let message = 'Pagamento com cartão processado com sucesso';
      if (scenario === 'success') {
        initialChargeStatus = 'paid';
      } else if (scenario === 'fail') {
        initialChargeStatus = 'failed';
        message = 'Transação não autorizada (cartão de falha)';
      } else if (scenario === 'processing_to_success' || scenario === 'processing_to_fail' || scenario === 'processing_to_canceled') {
        initialChargeStatus = 'processing';
        message = 'Transação em processamento';
      } else if (scenario === 'success_processing_to_canceled' || scenario === 'success_processing_back_to_paid' || scenario === 'paid_to_chargeback') {
        initialChargeStatus = 'paid';
        message = 'Transação aprovada';
      } else {
        // Cenário padrão (não teste): aprovado
        initialChargeStatus = 'paid';
      }

      return {
        status: 'success',
        data: {
          charge_id: chargeId,
          method: 'credit_card',
          installments: safeInstallments,
          installment_amount: installmentAmount,
          charge_status: initialChargeStatus
        },
        message
      };
    }

    return {
      status: 'error',
      message: 'Método de pagamento não suportado'
    };
  }

  static async tokenizeCard(cardData: any): Promise<{ id: string; status: string; data?: any }> {
    // Simular delay de rede
    await new Promise(resolve => setTimeout(resolve, 800));

    devLog('🎭 Mock: Tokenizando cartão...', {
      number: '****' + cardData.number.replace(/\s/g, '').slice(-4),
      holder: cardData.holder_name
    });

    // Validações básicas
    if (!cardData.number || !cardData.holder_name || !cardData.cvv) {
      throw new Error('Dados do cartão incompletos');
    }

    const cleanNumber = String(cardData.number).replace(/\D/g, '');
    const testInfo = PaymentValidator.getTestCardInfo(cleanNumber);

    // Simular tokenização
    const rand = Math.random().toString(36).substr(2, 9);
    const token = testInfo ? `testcard_${cleanNumber}_${rand}` : `card_token_${Date.now()}_${rand}`;
    devLog('✅ Mock: Token gerado com sucesso:', '****' + token.slice(-8));

    return {
      id: token,
      status: 'success',
      data: {
        card_token: token,
        last_four_digits: cleanNumber.slice(-4),
        brand: 'visa',
        test_card_number: testInfo ? cleanNumber : undefined,
        test_card_name: testInfo ? testInfo.name : undefined
      }
    };
  }

  static async getPaymentStatus(chargeId: string): Promise<string> {
    // Simular delay de rede
    await new Promise(resolve => setTimeout(resolve, 300));

    // Se houver cenário registrado, simular transições determinísticas baseado no tempo
    const scenarioEntry = this.chargeScenarios[chargeId];
    if (scenarioEntry) {
      const elapsed = (Date.now() - scenarioEntry.createdAt) / 1000; // segundos
      const s = scenarioEntry.scenario;

      switch (s) {
        case 'success':
          return 'paid';
        case 'fail':
          return 'failed';
        case 'processing_to_success':
          return elapsed < 5 ? 'processing' : 'paid';
        case 'processing_to_fail':
          return elapsed < 5 ? 'processing' : 'failed';
        case 'processing_to_canceled':
          return elapsed < 5 ? 'processing' : 'canceled';
        case 'success_processing_to_canceled':
          if (elapsed < 3) return 'paid';
          if (elapsed < 8) return 'processing';
          return 'canceled';
        case 'success_processing_back_to_paid':
          if (elapsed < 3) return 'paid';
          if (elapsed < 8) return 'processing';
          return 'paid';
        case 'paid_to_chargeback':
          // Durante a janela de compra, permanece 'paid'; depois de um tempo, vira chargeback
          if (elapsed < 60) return 'paid';
          return 'chargedback';
        default:
          return 'pending';
      }
    }

    // Fallback aleatório quando não há cenário registrado
    const random = Math.random();
    if (random < 0.7) {
      return 'pending';
    } else if (random < 0.9) {
      return 'paid';
    } else {
      return 'failed';
    }
  }
}
